import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Public as GlobalIcon,
    Home as CustomIcon,
} from '@mui/icons-material';
import Table from '../../components/Table/Table';
import ConfirmationDialog from "../../utils/confirmationDialog";

import {
    useDeletePresetRequestMutation,
    useCreatePresetRequestMutation,
    useUpdatePresetRequestMutation
} from '../../store/services/delivery-service';
import { useGetSimulationsQuery } from "../../store/services/simulation-service";
import { useGetCoursesQuery } from '../../store/services/university-service';


// Helper function to calculate date from month-day pattern
const calculateDateFromMonthDay = (startDate, relativeMonth, dayOfMonth) => {
    if (!startDate) return null;

    try {
        const result = new Date(startDate);

        // Add months (relativeMonth is 1-based, so subtract 1)
        result.setMonth(result.getMonth() + (relativeMonth - 1));

        // Check if the day exists in this month
        const daysInMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();

        // Adjust for month end if needed
        const targetDay = dayOfMonth > daysInMonth ? daysInMonth : dayOfMonth;

        result.setDate(targetDay);

        return result;
    } catch (error) {
        console.error("Error calculating date:", error);
        return null;
    }
};

const PresetDeliveryTable = ({ presetData, error, isLoading, showNotification, onViewCalendar }) => {

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 20,
        page: 0
    });

    // Items table pagination in the preview modal
    const [itemsPaginationModel, setItemsPaginationModel] = useState({
        pageSize: 20,
        page: 0
    });

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [dialogAction, setDialogAction] = useState(null);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogContent, setDialogContent] = useState('');
    const [selectedPresetId, setSelectedPresetId] = useState(null);
    const [presetDetailOpen, setPresetDetailOpen] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState(null);

    // State for create/edit preset dialog
    const [presetFormOpen, setPresetFormOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isGlobalPreset, setIsGlobalPreset] = useState(false);
    const [presetFormData, setPresetFormData] = useState({
        name: '',
        items: []
    });

    // Track removed items for backend updates
    const [removedItemIds, setRemovedItemIds] = useState([]);

    // For adding/editing items in the preset
    const [itemFormOpen, setItemFormOpen] = useState(false);
    const [isEditingItem, setIsEditingItem] = useState(false);
    const [currentItem, setCurrentItem] = useState({
        relativeMonth: 1,
        dayOfMonth: 1,
        contentType: 'Simulation',
        contentId: '',
        contentName: '',
        adjustForMonthEnd: true,
        adjustForWeekends: true
    });
    const [editingItemIndex, setEditingItemIndex] = useState(-1);

    // Add state for the start date
    const [startDate, setStartDate] = useState('');
    const [minStartDate, setMinStartDate] = useState('');

    const navigate = useNavigate();

    // Get user and tenancy information from Redux store
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { role } = useSelector((state) => state.auth.user || {});

    // Check if user is super admin
    const isSuperAdmin = role === "Administrators";

    // Set minimum start date when component mounts
    useEffect(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setMinStartDate(tomorrow.toISOString().split('T')[0]);
    }, []);

    // Validate if start date is valid (must be in the future)
    const isStartDateValid = startDate && new Date(startDate) > new Date(new Date().setHours(0, 0, 0, 0));

    // Get simulations and courses for content selection
    const { data: simulationData } = useGetSimulationsQuery({
        perPage: 500,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: ""
    });

    const { data: courseData } = useGetCoursesQuery({
        perPage: 500,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: ""
    });

    // API mutations
    const [deletePresetRequest, { isLoading: isDeleting }] = useDeletePresetRequestMutation();
    const [createPresetRequest, { isLoading: isCreating }] = useCreatePresetRequestMutation();
    const [updatePresetRequest, { isLoading: isUpdating }] = useUpdatePresetRequestMutation();

    // Reset form data
    const resetFormData = () => {
        setPresetFormData({
            name: '',
            items: []
        });
        setIsGlobalPreset(false);
        setRemovedItemIds([]); // Reset removed items tracking
    };

    // Handle opening the create preset dialog
    const handleCreatePresetDialog = (isGlobal = false) => {
        setIsEditMode(false);
        resetFormData();
        setIsGlobalPreset(isGlobal);
        setPresetFormOpen(true);
    };

    // Handle opening the edit preset dialog
    const handleEditPresetDialog = (preset) => {
        setIsEditMode(true);
        setPresetFormData({
            id: preset.id,
            name: preset.name,
            items: [...preset.items],
            tenancyId: preset.tenancyId
        });
        setIsGlobalPreset(preset.tenancyId === 0);
        setRemovedItemIds([]); // Reset removed items tracking when opening edit dialog
        setPresetFormOpen(true);
    };

    // Handle creating or updating a preset
    const handleSavePreset = async () => {
        try {
            if (isEditMode) {
                const presetData = {
                    presetId: presetFormData.id,
                    name: presetFormData.name,
                    tenancyId: isGlobalPreset ? 0 : tenancyId,
                    newItems: presetFormData.items.filter(item => !item.id).map(item => ({
                        contentId: item.contentId,
                        contentName: item.contentName,
                        contentType: item.contentType,
                        relativeMonth: item.relativeMonth,
                        dayOfMonth: item.dayOfMonth,
                        adjustForMonthEnd: item.adjustForMonthEnd !== undefined ? item.adjustForMonthEnd : true,
                        adjustForWeekends: item.adjustForWeekends !== undefined ? item.adjustForWeekends : true
                    })),
                    itemUpdates: presetFormData.items.filter(item => item.id).map(item => ({
                        itemId: item.id,
                        contentId: item.contentId,
                        contentType: item.contentType,
                        relativeMonth: item.relativeMonth,
                        dayOfMonth: item.dayOfMonth,
                        adjustForMonthEnd: item.adjustForMonthEnd,
                        adjustForWeekends: item.adjustForWeekends
                    })),
                    itemIdsToRemove: removedItemIds
                };

                const requestPayload = {
                    id: presetFormData.id,
                    presetData: presetData  
                };

                console.log("Sending update request:", JSON.stringify(requestPayload, null, 2));

                await updatePresetRequest(requestPayload).unwrap();
                showNotification('Template updated successfully', 'success');
            } else {
                await createPresetRequest({
                    name: presetFormData.name,
                    presetItems: presetFormData.items.map(item => ({
                        contentId: item.contentId,  
                        contentType: item.contentType,
                        relativeMonth: item.relativeMonth,
                        dayOfMonth: item.dayOfMonth,
                        adjustForMonthEnd: item.adjustForMonthEnd !== undefined ? item.adjustForMonthEnd : true,
                        adjustForWeekends: item.adjustForWeekends !== undefined ? item.adjustForWeekends : false
                    })),
                    tenancyId: isGlobalPreset ? 0 : tenancyId
                }).unwrap();
                showNotification('Template created successfully', 'success');
            }
            setPresetFormOpen(false);
            setRemovedItemIds([]); 
        } catch (err) {
            console.error("Error saving preset:", err);
            showNotification('Failed to save template: ' + (err.data?.message || err.message || 'Unknown error'), 'error');
        }
    };

    // Handle opening the item form dialog for adding/editing an item
    const handleItemFormDialog = (item = null, index = -1) => {
        if (item) {
            // When editing, make a deep copy of the item to avoid reference issues
            setCurrentItem({ ...item });

            console.log("Editing item at index:", index);

            setEditingItemIndex(index);
            setIsEditingItem(true);
        } else {
            // Reset for new item
            setCurrentItem({
                relativeMonth: 1,
                dayOfMonth: 1,
                contentType: 'Simulation',
                contentId: '',
                contentName: '',
                adjustForMonthEnd: true,
                adjustForWeekends: true
            });
            setEditingItemIndex(-1);
            setIsEditingItem(false);
        }
        setItemFormOpen(true);
    };

    // Handle content type change
    const handleContentTypeChange = (e) => {
        setCurrentItem(prev => ({
            ...prev,
            contentType: e.target.value,
            contentId: '',
            contentName: ''
        }));
    };

    // Handle content selection
    const handleContentSelection = (e) => {
        const selectedName = e.target.value;
        const selectedContent = currentItem.contentType === 'Simulation'
            ? simulationData?.find(sim => sim.simulationName === selectedName)
            : courseData?.find(course => course.courseName === selectedName);

        setCurrentItem(prev => ({
            ...prev,
            contentId: selectedName,
            contentName: selectedName
        }));
    };

    // Handle saving an item to the preset
    const handleSaveItem = () => {
        if (!currentItem.contentId) {
            showNotification('Please select content', 'error');
            return;
        }

        // Create a copy of the items array
        const updatedItems = [...presetFormData.items];

        console.log("Saving item with editing state:", {
            isEditingItem,
            editingItemIndex,
            itemCount: updatedItems.length,
            currentItem
        });

        if (isEditingItem && editingItemIndex >= 0) {
            // Update existing item at the specified index
            console.log("Updating existing item at index:", editingItemIndex);

            if (editingItemIndex >= updatedItems.length) {
                console.error("Index out of bounds:", editingItemIndex, "Array length:", updatedItems.length);
                showNotification('Error updating item: index out of bounds', 'error');
                return;
            }

            updatedItems[editingItemIndex] = { ...currentItem };
        } else {
            // Add new item
            console.log("Adding new item");
            updatedItems.push({ ...currentItem });
        }

        // Update the form data with the modified items array
        setPresetFormData({
            ...presetFormData,
            items: updatedItems
        });

        // Reset form state
        setItemFormOpen(false);
        setIsEditingItem(false);
        setEditingItemIndex(-1);

        showNotification(
            isEditingItem ? 'Item updated successfully' : 'Item added successfully',
            'success'
        );
    };

    // Handle removing an item from the preset
    const handleRemoveItem = (index) => {
        const updatedItems = [...presetFormData.items];
        const removedItem = updatedItems[index];

        // If the item has an ID (existing item), add it to removedItemIds
        if (removedItem.id) {
            console.log("Tracking removed item with ID:", removedItem.id);
            setRemovedItemIds(prev => [...prev, removedItem.id]);
        }

        // Remove the item from the items array
        updatedItems.splice(index, 1);

        setPresetFormData({
            ...presetFormData,
            items: updatedItems
        });

        showNotification('Item removed', 'info');
    };

    // Handle deleting a preset template
    const handleDeletePreset = (preset) => {
        setSelectedPresetId(preset.id);
        setDialogTitle('Delete Template');
        setDialogContent('Are you sure you want to delete this template? This action cannot be undone.');
        setDialogAction(() => async () => {
            try {
                await deletePresetRequest(preset.id).unwrap();
                showNotification('Template deleted successfully', 'success');
            } catch (err) {
                showNotification('Failed to delete template: ' + (err.data?.message || 'Unknown error'), 'error');
            }
        });
        setConfirmDialogOpen(true);
    };

    // Handle viewing preset details
    const handleViewPreset = (preset) => {
        setSelectedPreset(preset);
        setPresetDetailOpen(true);
        // Reset the items pagination when opening the preview modal
        setItemsPaginationModel({
            pageSize: 20,
            page: 0
        });
        // Reset start date when opening the preview
        setStartDate('');
    };

    // Format date as string
    const formatDate = (date) => {
        if (!date) return "";
        return date.toLocaleDateString();
    };

    // Check if user can edit this preset
    const canEditPreset = (preset) => {
        // Super admins can edit any preset
        if (isSuperAdmin) return true;

        // Regular tenancy admins can only edit their own custom presets
        return preset.tenancyId !== 0 && preset.tenancyId === tenancyId;
    };

    // Check if user can delete this preset
    const canDeletePreset = (preset) => {
        // Super admins can delete any preset
        if (isSuperAdmin) return true;

        // Regular tenancy admins can only delete their own custom presets
        return preset.tenancyId !== 0 && preset.tenancyId === tenancyId;
    };

    const headCells = [
        {
            id: "preview",
            label: "",
            render: (dataRow) => (
                <Button
                    onClick={() => handleViewPreset(dataRow)}
                    size="small"
                    variant="contained"
                    color="primary"
                >
                    Preview
                </Button>
            ),
            initialVisible: true
        },
        {
            id: "edit",
            label: "",
            render: (dataRow) => {
                if (!canEditPreset(dataRow)) return null;

                return (
                    <Button
                        onClick={() => handleEditPresetDialog(dataRow)}
                        size="small"
                        variant="contained"
                        color="secondary"
                    >
                        Edit
                    </Button>
                );
            },
            initialVisible: true
        },
        {
            id: 'name',
            label: 'Name',
            initialVisible: true
        },
        {
            id: 'itemCount',
            label: 'Items',
            initialVisible: true,
            render: (row) => row.items?.length || 0
        },
        {
            id: 'type',
            label: 'Type',
            initialVisible: true,
            render: (row) => (
                <Chip
                    icon={row.tenancyId === 0 ? <GlobalIcon /> : <CustomIcon />}
                    label={row.tenancyId === 0 ? "Global" : "Custom"}
                    color={row.tenancyId === 0 ? "primary" : "default"}
                    size="small"
                />
            )
        },
        {
            id: 'createdAt',
            label: 'Created At',
            initialVisible: true,
            render: (row) => new Date(row.createdAt).toLocaleString()
        },
        {
            id: 'updatedAt',
            label: 'Updated At',
            initialVisible: true,
            render: (row) => new Date(row.updatedAt).toLocaleString()
        },
        {
            id: "delete",
            label: "",
            render: (dataRow) => {
                if (!canDeletePreset(dataRow)) return null;

                return (
                    <Button
                        onClick={() => handleDeletePreset(dataRow)}
                        size="small"
                        variant="contained"
                        color="error"
                        disabled={isDeleting && selectedPresetId === dataRow.id}
                    >
                        {isDeleting && selectedPresetId === dataRow.id ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            "Delete"
                        )}
                    </Button>
                );
            },
            initialVisible: true
        },
    ];

    const itemsHeadCells = [
        {
            id: 'relativeMonth',
            label: 'Month',
            initialVisible: true,
            render: (row) => (
                <Chip
                    label={`Month ${row.relativeMonth}`}
                    color="default"
                    variant="outlined"
                    size="small"
                />
            )
        },
        {
            id: 'dayOfMonth',
            label: 'Day',
            initialVisible: true,
            render: (row) => (
                <Chip
                    label={`Day ${row.dayOfMonth}`}
                    color="secondary"
                    variant="outlined"
                    size="small"
                />
            )
        },
        {
            id: 'contentType',
            label: 'Content Type',
            initialVisible: true,
            render: (row) => (
                <Chip
                    label={row.contentType}
                    color={row.contentType === 'Simulation' ? 'primary' : 'warning'}
                    size="small"
                />
            )
        },
        {
            id: 'contentId',
            label: 'Content Name',
            initialVisible: true,
            render: (row) => row.contentName || row.contentId
        },
        {
            id: 'scheduledDate',
            label: 'Scheduled Date',
            initialVisible: true,
            render: (row) => {
                if (!startDate) return "-";

                try {
                    // Calculate the scheduled date using month-day pattern
                    const scheduledDate = calculateDateFromMonthDay(
                        new Date(startDate),
                        row.relativeMonth,
                        row.dayOfMonth
                    );
                    return formatDate(scheduledDate);
                } catch (error) {
                    console.error("Error calculating scheduled date:", error);
                    return "-";
                }
            }
        }
    ];

    const editItemsHeadCells = [
        {
            id: 'edit',
            label: '',
            initialVisible: true,
            render: (row, _, tableIndex) => {
                const actualIndex = presetFormData.items.findIndex(item =>
                    item.contentId === row.contentId &&
                    item.contentType === row.contentType &&
                    item.relativeMonth === row.relativeMonth &&
                    item.dayOfMonth === row.dayOfMonth
                );

                return (
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => handleItemFormDialog(row, actualIndex)}
                    >
                        Edit
                    </Button>
                );
            }
        },
        ...itemsHeadCells.filter(cell => cell.id !== 'scheduledDate').map(cell => ({
            ...cell,
            initialVisible: true 
        })),
        {
            id: 'delete',
            label: '',
            initialVisible: true,
            render: (row, _, tableIndex) => {
                const actualIndex = presetFormData.items.findIndex(item =>
                    item.contentId === row.contentId &&
                    item.contentType === row.contentType &&
                    item.relativeMonth === row.relativeMonth &&
                    item.dayOfMonth === row.dayOfMonth
                );

                return (
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleRemoveItem(actualIndex)}
                    >
                        Remove
                    </Button>
                );
            }
        }
    ];

    // Toolbar actions
    const toolbarActions = [
        {
            render: () => (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        endIcon={<AddIcon />}
                        onClick={() => handleCreatePresetDialog(false)}
                    >
                        New Custom Template
                    </Button>
                    {isSuperAdmin && (
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={<GlobalIcon />}
                            onClick={() => handleCreatePresetDialog(true)}
                        >
                            New Global Template
                        </Button>
                    )}
                </Box>
            )
        }
    ];

    return (
        <>
            <Table
                name="Journey Templates"
                data={presetData?.presets || []}
                error={error}
                loading={isLoading}
                headCells={headCells}
                search={true}
                selected={[]}
                setSelected={() => { }}
                checkbox={false}
                toolbarActions={toolbarActions}
                showToolbarActions={true}
                maxTableHeight="calc(100vh - 280px)"
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                rowCount={presetData?.totalRecords || 0}
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
            />

            {/* Preview Preset Dialog */}
            <Dialog
                open={presetDetailOpen}
                onClose={() => setPresetDetailOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        width: '80%',
                        height: '90vh',
                        maxWidth: '1200px',
                        maxHeight: '1000px',
                        bgcolor: 'background.default'
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            {selectedPreset?.name}
                            {' '}
                            <Chip
                                icon={selectedPreset?.tenancyId === 0 ? <GlobalIcon /> : <CustomIcon />}
                                label={selectedPreset?.tenancyId === 0 ? "Global Template" : "Custom Template"}
                                color={selectedPreset?.tenancyId === 0 ? "primary" : "default"}
                                size="small"
                                sx={{ ml: 1 }}
                            />
                        </Typography>
                        {canEditPreset(selectedPreset) && (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    setPresetDetailOpen(false);
                                    handleEditPresetDialog(selectedPreset);
                                }}
                            >
                                Edit Template
                            </Button>
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ overflowY: 'auto' }}>
                    {selectedPreset && (
                        <>
                            {/* Title and date picker in one row */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                justifyContent: 'space-between',
                                mt: 1,
                                mb: 1
                            }}>
                                <Typography variant="h6" sx={{ mb: { xs: 2, sm: 0 } }}>
                                    Content Items ({selectedPreset.items?.length || 0})
                                </Typography>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                    <TextField
                                        label="Start Date"
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            min: minStartDate,
                                        }}
                                        error={!!startDate && !isStartDateValid}
                                        helperText={startDate && !isStartDateValid ? "Please select a future date" : ""}
                                        sx={{
                                            width: '200px'
                                        }}
                                        size="small"
                                    />
                                    {startDate && isStartDateValid && (
                                        <Button
                                            variant="outlined"
                                            size="medium"
                                            onClick={() => setStartDate('')}
                                            sx={{
                                                ml: { xs: 0, sm: 1 },
                                                mt: { xs: 1, sm: 0 },
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </Box>
                            </Box>

                            {/* Date explanation */}
                            {!startDate && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Select a start date to see when each item would be scheduled.
                                    </Typography>
                                </Box>
                            )}

                            {/* Replaced the direct table with the custom Table component */}
                            <Table
                                name=""
                                data={selectedPreset.items || []}
                                error={null}
                                loading={false}
                                headCells={itemsHeadCells}
                                search={false}
                                selected={[]}
                                setSelected={() => { }}
                                checkbox={false}
                                toolbarActions={[]}
                                showToolbarActions={false}
                                maxTableHeight="535px"
                                paginationModel={itemsPaginationModel}
                                setPaginationModel={setItemsPaginationModel}
                                rowCount={selectedPreset.items?.length || 0}
                                disablePagination={false}
                                initialVisibleColumns={itemsHeadCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                                size="small"
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setPresetDetailOpen(false)}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create/Edit Preset Dialog */}
            <Dialog
                open={presetFormOpen}
                onClose={() => setPresetFormOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        width: '80%',
                        height: '90vh',
                        maxWidth: '1200px',
                        maxHeight: '1000px',
                        bgcolor: 'background.default'
                    }
                }}
            >
                <DialogTitle>
                    {isEditMode ? 'Edit Template' : 'Create New Template'}
                    {' '}
                    <Chip
                        icon={isGlobalPreset ? <GlobalIcon /> : <CustomIcon />}
                        label={isGlobalPreset ? "Global Template" : "Custom Template"}
                        color={isGlobalPreset ? "primary" : "default"}
                        size="small"
                        sx={{ ml: 1 }}
                    />
                </DialogTitle>
                <DialogContent sx={{ overflowY: 'auto' }}>
                    <Box sx={{ mt: 2, mb: 3 }}>
                        <TextField
                            label="Template Name"
                            value={presetFormData.name}
                            onChange={(e) => setPresetFormData({ ...presetFormData, name: e.target.value })}
                            fullWidth
                            required
                            error={!presetFormData.name}
                            helperText={!presetFormData.name ? "Template name is required" : ""}
                        />
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }}>
                        <Typography variant="h6">
                            Content Items ({presetFormData.items?.length || 0})
                        </Typography>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => handleItemFormDialog()}
                        >
                            Add Item
                        </Button>
                    </Box>

                    {/* Items table */}
                    <Table
                        name=""
                        data={presetFormData.items || []}
                        error={null}
                        loading={false}
                        headCells={editItemsHeadCells}
                        search={false}
                        selected={[]}
                        setSelected={() => { }}
                        checkbox={false}
                        toolbarActions={[]}
                        showToolbarActions={false}
                        maxTableHeight="535px"
                        paginationModel={itemsPaginationModel}
                        setPaginationModel={setItemsPaginationModel}
                        rowCount={presetFormData.items?.length || 0}
                        disablePagination={false}
                        size="small"
                        initialVisibleColumns={editItemsHeadCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                        emptyMessage="No items added. Click 'Add Item' to create your first item."
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => setPresetFormOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSavePreset}
                        variant="contained"
                        color="primary"
                        disabled={!presetFormData.name || presetFormData.items.length === 0 || isCreating || isUpdating}
                    >
                        {isCreating || isUpdating ? <CircularProgress size={24} /> : 'Save Template'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Item Dialog */}
            <Dialog
                open={itemFormOpen}
                onClose={() => setItemFormOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { bgcolor: 'background.default' }
                }}
            >
                <DialogTitle>
                    {isEditingItem ? 'Edit Content Item' : 'Add Content Item'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                        {/* Left column - Scheduling */}
                        <Box>
                            <Typography variant="h6" gutterBottom>Scheduling</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Box>
                                    <TextField
                                        label="Month"
                                        type="number"
                                        value={currentItem.relativeMonth}
                                        onChange={(e) => setCurrentItem({
                                            ...currentItem,
                                            relativeMonth: Math.max(1, parseInt(e.target.value, 10) || 1)
                                        })}
                                        fullWidth
                                        inputProps={{ min: 1 }}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Month 1 is the first month. Use 2, 3, etc. for subsequent months.
                                    </Typography>
                                </Box>

                                <Box>
                                    <TextField
                                        label="Day of Month"
                                        type="number"
                                        value={currentItem.dayOfMonth}
                                        onChange={(e) => setCurrentItem({
                                            ...currentItem,
                                            dayOfMonth: Math.max(1, Math.min(31, parseInt(e.target.value, 10) || 1))
                                        })}
                                        fullWidth
                                        inputProps={{ min: 1, max: 31 }}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Day of the month (1-31)
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Right column - Content Selection */}
                        <Box>
                            <Typography variant="h6" gutterBottom>Content</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Box>
                                    <FormControl fullWidth sx={{ mb: 1 }}>
                                        <InputLabel>Content Type</InputLabel>
                                        <Select
                                            value={currentItem.contentType}
                                            label="Content Type"
                                            onChange={handleContentTypeChange}
                                        >
                                            <MenuItem value="Simulation">Simulation</MenuItem>
                                            <MenuItem value="Course">Course</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary">
                                        Select the type of content to add
                                    </Typography>
                                </Box>

                                <Box>
                                    <FormControl fullWidth sx={{ mb: 1 }}>
                                        <InputLabel>Select {currentItem.contentType}</InputLabel>
                                        <Select
                                            value={currentItem.contentId}
                                            label={`Select ${currentItem.contentType}`}
                                            onChange={handleContentSelection}
                                        >
                                            {currentItem.contentType === 'Simulation' && simulationData && simulationData.map((sim, index) => (
                                                <MenuItem key={index} value={sim.simulationName}>
                                                    {sim.simulationName}
                                                </MenuItem>
                                            ))}
                                            {currentItem.contentType === 'Course' && courseData && courseData.map((course, index) => (
                                                <MenuItem key={index} value={course.courseName}>
                                                    {course.courseName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary">
                                        Choose a specific content item
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => setItemFormOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveItem}
                        variant="contained"
                        color="primary"
                        disabled={!currentItem.contentId}
                    >
                        {isEditingItem ? 'Update Item' : 'Add Item'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                open={confirmDialogOpen}
                title={dialogTitle}
                content={dialogContent}
                onConfirm={() => {
                    setConfirmDialogOpen(false);
                    dialogAction?.();
                }}
                onCancel={() => setConfirmDialogOpen(false)}
            />
        </>
    );
};

PresetDeliveryTable.propTypes = {
    showNotification: PropTypes.func.isRequired
};

export default PresetDeliveryTable;